import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  Badge,
  VStack,
  HStack,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Center,
  Spinner,
  Icon,
  IconButton,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  FaTruck,
  FaUser,
  FaCheck,
  FaPlus,
  FaMinus,
  FaTrash,
  FaCalendarAlt,
  FaGasPump,
} from "react-icons/fa";
import { MdLocalShipping } from "react-icons/md";
import {
  fetchFrotaAtendimento,
  resolveMotoristaDoVeiculo,
  getVeiculoPorId,
} from "../apis/frota-supabase-api";
import VeiculoAutocomplete from "./veiculo-autocomplete";
import TruckCompartmentsVisual from "./truck-compartments-visual";
import { isLocalhostDev } from "../utils/is-localhost-dev";
import { buildCombustivelCatalog } from "./carrinho-produtos-atendimento";

const API_PRECOS = "/api/frota/historico-precos";

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayBR() {
  const [y, m, d] = todayISO().split("-");
  return `${d}/${m}/${y}`;
}

const TOTAL_STEPS = 4;

const STEPS = [
  { id: 1, title: "Frota", subtitle: "Um ou mais caminhões" },
  { id: 2, title: "Motorista", subtitle: "Confirme o motorista" },
  { id: 3, title: "Produto e carga", subtitle: "Volume e compartimentos" },
  { id: 4, title: "Resumo", subtitle: "Revise e conclua" },
];

const fieldLabelProps = {
  color: "#007abe",
  fontWeight: "semibold",
  fontSize: "xs",
  mb: 1,
  textTransform: "uppercase",
  letterSpacing: "wider",
};

const inputProps = {
  focusBorderColor: "#007abe",
  borderColor: "gray.300",
  bg: "gray.50",
  fontSize: "sm",
  borderRadius: "lg",
  _hover: { borderColor: "#007abe" },
};

const OrdemCarregamentoPanel = ({
  rowItem = null,
  produtosPedido = [],
  veiculosSelecionados = [],
  onVeiculosChange,
  /** @deprecated use veiculosSelecionados — mantido para compat */
  veiculoSelecionado = "",
  onVeiculoChange,
  motoristaNome = "",
  motoristaCpf = "",
  motoristaId = "",
  onMotoristaNomeChange,
  onMotoristaChange,
}) => {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [veiculos, setVeiculos] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [vinculos, setVinculos] = useState([]);
  const [loadingFrota, setLoadingFrota] = useState(true);
  const [frotaErro, setFrotaErro] = useState("");

  const [veiculosIds, setVeiculosIds] = useState(() => {
    if (Array.isArray(veiculosSelecionados) && veiculosSelecionados.length) {
      return veiculosSelecionados;
    }
    return veiculoSelecionado ? [veiculoSelecionado] : [];
  });
  const [veiculoAtivoId, setVeiculoAtivoId] = useState(
    () =>
      (Array.isArray(veiculosSelecionados) && veiculosSelecionados[0]) ||
      veiculoSelecionado ||
      "",
  );
  /** { [veiculoId]: { id, nome, cpf, automatico } } */
  const [motoristasMap, setMotoristasMap] = useState({});

  const [dataCarregamento, setDataCarregamento] = useState(
    new Date().toISOString().slice(0, 10),
  );

  const [produtoCodigo, setProdutoCodigo] = useState("");
  const [produtoDesc, setProdutoDesc] = useState("");
  const [volume, setVolume] = useState("");
  const [compartimentosSel, setCompartimentosSel] = useState([]);

  const [itensOrdem, setItensOrdem] = useState([]);
  const [ordemConfirmada, setOrdemConfirmada] = useState(null);
  const [produtosPreco, setProdutosPreco] = useState([]);
  const [loadingPrecos, setLoadingPrecos] = useState(false);
  const [produtoPrecoSel, setProdutoPrecoSel] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoadingFrota(true);
      setFrotaErro("");
      try {
        const { veiculos: vList, motoristas: mList, vinculos: vinList } =
          await fetchFrotaAtendimento();
        setVeiculos(vList);
        setMotoristas(mList);
        setVinculos(vinList);
      } catch (e) {
        console.error(e);
        setFrotaErro(e.message || "Erro ao carregar frota");
        setVeiculos([]);
        setMotoristas([]);
        setVinculos([]);
        toast({
          title: "Frota indisponível",
          description: e.message,
          status: "error",
          isClosable: true,
        });
      } finally {
        setLoadingFrota(false);
      }
    };
    load();
  }, [toast]);

  useEffect(() => {
    const loadPrecos = async () => {
      setLoadingPrecos(true);
      const hoje = todayISO();
      try {
        const res = await fetch(
          `${API_PRECOS}?de=${encodeURIComponent(hoje)}&ate=${encodeURIComponent(hoje)}`,
        );
        if (!res.ok) throw new Error("Erro ao carregar preços do dia");
        const data = await res.json();
        setProdutosPreco(buildCombustivelCatalog(Array.isArray(data) ? data : [], hoje));
      } catch (e) {
        console.error(e);
        setProdutosPreco([]);
        toast({
          title: "Preços indisponíveis",
          description:
            e.message ||
            `Não foi possível carregar combustíveis de ${todayBR()}`,
          status: "warning",
          isClosable: true,
        });
      } finally {
        setLoadingPrecos(false);
      }
    };
    loadPrecos();
  }, [toast]);

  const syncVeiculos = (ids) => {
    setVeiculosIds(ids);
    onVeiculosChange?.(ids);
    onVeiculoChange?.(ids[0] || "");
  };

  const veiculosEscolhidos = useMemo(
    () =>
      veiculosIds
        .map((id) => getVeiculoPorId(veiculos, id))
        .filter(Boolean),
    [veiculos, veiculosIds],
  );

  const veiculo = useMemo(
    () => getVeiculoPorId(veiculos, veiculoAtivoId),
    [veiculos, veiculoAtivoId],
  );

  const capacidadeTotalFrota = useMemo(
    () =>
      veiculosEscolhidos.reduce((sum, v) => {
        const caps = Array.isArray(v.litros_por_boca) ? v.litros_por_boca : [];
        return sum + caps.reduce((s, l) => s + (Number(l) || 0), 0);
      }, 0),
    [veiculosEscolhidos],
  );

  const adicionarVeiculo = (id) => {
    if (!id) return;
    if (veiculosIds.includes(id)) {
      setVeiculoAtivoId(id);
      toast({
        title: "Veículo já selecionado",
        status: "info",
        duration: 2000,
      });
      return;
    }
    const next = [...veiculosIds, id];
    syncVeiculos(next);
    setVeiculoAtivoId(id);
    setCompartimentosSel([]);
  };

  const removerVeiculo = (id) => {
    const next = veiculosIds.filter((x) => x !== id);
    syncVeiculos(next);
    setItensOrdem((prev) => prev.filter((it) => it.veiculoId !== id));
    setMotoristasMap((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    setVeiculoAtivoId((atual) => {
      if (atual !== id) return atual;
      return next[0] || "";
    });
    setCompartimentosSel([]);
  };

  useEffect(() => {
    if (!veiculosIds.length) {
      setMotoristasMap({});
      onMotoristaNomeChange?.("");
      onMotoristaChange?.(null);
      return;
    }
    setMotoristasMap((prev) => {
      const next = {};
      veiculosIds.forEach((id) => {
        const resolved = resolveMotoristaDoVeiculo(id, {
          vinculos,
          motoristas,
        });
        if (resolved) {
          next[id] = {
            id: resolved.id || "",
            nome: resolved.nome || "",
            cpf: resolved.cpf || "",
            automatico: true,
          };
        } else if (prev[id] && !prev[id].automatico) {
          next[id] = prev[id];
        } else {
          next[id] = { id: "", nome: "", cpf: "", automatico: false };
        }
      });
      return next;
    });
    setCompartimentosSel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veiculosIds.join("|"), vinculos, motoristas]);

  useEffect(() => {
    const first = motoristasMap[veiculosIds[0]];
    if (first?.nome) {
      onMotoristaChange?.({
        id: first.id,
        nome: first.nome,
        cpf: first.cpf,
      });
    } else {
      onMotoristaNomeChange?.("");
      onMotoristaChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motoristasMap, veiculosIds]);

  const setMotoristaDoVeiculo = (veiculoId, motoristaData) => {
    setMotoristasMap((prev) => ({
      ...prev,
      [veiculoId]: {
        id: motoristaData?.id || "",
        nome: motoristaData?.nome || "",
        cpf: motoristaData?.cpf || "",
        automatico: false,
      },
    }));
  };

  const produtosDisponiveis = useMemo(() => {
    const fromPrecos = produtosPreco.map((p) => ({
      codigo: p.codigo,
      descricao: p.descricao,
      precoUnitario: p.precoUnitario,
      fornecedor: p.fornecedor,
      fornecedorId: p.fornecedorId,
      dataPreco: p.dataPreco,
      key: p.key,
      origem: "historico-precos",
    }));

    const fromPedido = produtosPedido.map((p) => ({
      codigo: p.codigo,
      descricao: p.descricao,
      precoUnitario: p.precoUnitario ?? null,
      fornecedor: p.fornecedor || "",
      key: `pedido-${p.codigo}`,
      origem: "pedido",
    }));

    const seen = new Set();
    const merged = [];
    [...fromPrecos, ...fromPedido].forEach((p) => {
      const id = p.key || p.codigo;
      if (!id || seen.has(id)) return;
      seen.add(id);
      merged.push(p);
    });
    return merged;
  }, [produtosPedido, produtosPreco]);

  const getCapacidadeBoca = (num) => {
    const arr = veiculo?.litros_por_boca || [];
    return Number(arr[num - 1]) || 0;
  };

  const isBocaIncompativel = (num, vol) => {
    const cap = getCapacidadeBoca(num);
    const v = Number(vol) || 0;
    return v > 0 && v > cap;
  };

  const toggleCompartimento = (num) => {
    const vol = Number(volume) || 0;
    if (isBocaIncompativel(num, vol)) return;
    setCompartimentosSel((prev) =>
      prev.includes(num)
        ? prev.filter((n) => n !== num)
        : [...prev, num].sort((a, b) => a - b),
    );
  };

  const handleBocaIncompativel = (num, cap) => {
    const vol = Number(volume) || 0;
    toast({
      title: `C${num} não comporta este volume`,
      description: `${vol.toLocaleString("pt-BR")} L excede a capacidade de ${cap.toLocaleString("pt-BR")} L`,
      status: "error",
      duration: 4000,
      isClosable: true,
    });
  };

  useEffect(() => {
    const vol = Number(volume) || 0;
    if (vol <= 0) return;
    setCompartimentosSel((prev) =>
      prev.filter((n) => !isBocaIncompativel(n, vol)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volume, veiculo]);

  const adjustVolume = (delta) => {
    setVolume((prev) => {
      const current = Number(prev) || 0;
      return String(Math.max(0, current + delta));
    });
  };

  const volumeTotalOrdem = itensOrdem.reduce(
    (s, i) => s + (Number(i.volume) || 0),
    0,
  );

  const volumeTotalPedido = useMemo(() => {
    if (rowItem?.volumeTotal != null && Number(rowItem.volumeTotal) > 0) {
      return Number(rowItem.volumeTotal);
    }
    return (produtosPedido || []).reduce(
      (s, p) => s + (Number(p.volume) || 0),
      0,
    );
  }, [rowItem, produtosPedido]);

  const volumeRestantePedido = Math.max(
    0,
    volumeTotalPedido - volumeTotalOrdem,
  );

  const motoristasCompletos = veiculosIds.every(
    (id) => Boolean(motoristasMap[id]?.nome),
  );

  const canNext = () => {
    if (isLocalhostDev()) return true;
    if (step === 1) return veiculosIds.length > 0;
    if (step === 2)
      return motoristasCompletos && Boolean(dataCarregamento);
    if (step === 3) return itensOrdem.length > 0;
    return true;
  };

  const adicionarItem = () => {
    const vol = Number(volume) || 0;
    if (!veiculoAtivoId || !veiculo) {
      toast({
        title: "Selecione o caminhão para alocar",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    if (!produtoDesc || vol <= 0 || compartimentosSel.length === 0) {
      toast({
        title: "Preencha produto, volume e compartimentos",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    const bocasInvalidas = compartimentosSel.filter((n) =>
      isBocaIncompativel(n, vol),
    );
    if (bocasInvalidas.length > 0) {
      toast({
        title: "Volume excede a capacidade da boca",
        description: `Remova C${bocasInvalidas.join(", C")} — volume maior que o permitido`,
        status: "error",
        duration: 4000,
      });
      return;
    }
    const mot = motoristasMap[veiculoAtivoId];
    setItensOrdem((prev) => [
      ...prev,
      {
        id: Date.now(),
        produto: produtoCodigo || produtoDesc,
        produtoDesc,
        volume: Number(volume),
        compartimentos: [...compartimentosSel],
        precoUnitario: produtoPrecoSel?.precoUnitario ?? null,
        fornecedor: produtoPrecoSel?.fornecedor || "",
        dataPreco: produtoPrecoSel?.dataPreco || null,
        veiculoId: veiculoAtivoId,
        placa: veiculo.placa || "",
        motoristaNome: mot?.nome || "",
      },
    ]);
    setProdutoCodigo("");
    setProdutoDesc("");
    setProdutoPrecoSel(null);
    setVolume("");
    setCompartimentosSel([]);
    toast({ title: "Item adicionado à ordem", status: "success", duration: 2000 });
  };

  const confirmarOrdem = () => {
    if (itensOrdem.length === 0) {
      toast({
        title: "Adicione ao menos um item",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    const placas = veiculosEscolhidos.map((v) => v.placa).filter(Boolean);
    const nomesMotoristas = veiculosIds
      .map((id) => motoristasMap[id]?.nome)
      .filter(Boolean);
    const ordem = {
      numero: String(Date.now()).slice(-5),
      cliente:
        rowItem?.cliente || rowItem?.nome || rowItem?.contato || "—",
      clienteCodigo: rowItem?.codigoCliente || rowItem?.codigo || "—",
      data: dataCarregamento,
      placa: placas.join(", "),
      placas,
      veiculos: veiculosEscolhidos.map((v) => ({
        id: v.id,
        placa: v.placa,
        motorista: motoristasMap[v.id]?.nome || "",
        motoristaCpf: motoristasMap[v.id]?.cpf || "",
      })),
      motorista: nomesMotoristas.join(", "),
      motoristaCpf: motoristasMap[veiculosIds[0]]?.cpf || "",
      itens: itensOrdem,
      volumeTotal: volumeTotalOrdem,
      criadoEm: new Date().toISOString(),
    };
    setOrdemConfirmada(ordem);
    toast({
      title: "Ordem de carregamento gerada",
      description: `#${ordem.numero}`,
      status: "success",
      duration: 4000,
    });
  };

  const StepperLateral = () => (
    <VStack align="stretch" spacing={0} w="full">
      <Box
        bg="#007abe"
        color="white"
        px={4}
        py={3}
        borderTopRadius="lg"
        fontWeight="bold"
        fontSize="sm"
      >
        Etapas
      </Box>
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderTop="none"
        borderBottomRadius="lg"
        bg="white"
        p={2}
      >
        {STEPS.map((s) => {
          const done = step > s.id || (s.id === TOTAL_STEPS && ordemConfirmada);
          const active = step === s.id;
          return (
            <Flex
              key={s.id}
              align="center"
              gap={3}
              p={3}
              borderRadius="md"
              bg={active ? "blue.50" : "transparent"}
              cursor="pointer"
              onClick={() => s.id < step && setStep(s.id)}
              _hover={{ bg: active ? "blue.50" : "gray.50" }}
              mb={1}
            >
              <Flex
                w="28px"
                h="28px"
                borderRadius="full"
                bg={done ? "green.500" : active ? "#007abe" : "gray.200"}
                color="white"
                align="center"
                justify="center"
                fontSize="xs"
                fontWeight="bold"
                flexShrink={0}
              >
                {done ? <FaCheck size={12} /> : s.id}
              </Flex>
              <Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.800">
                  {s.title}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {s.subtitle}
                </Text>
              </Box>
            </Flex>
          );
        })}
      </Box>
    </VStack>
  );

  const TruckVisual = () => (
    <TruckCompartmentsVisual
      veiculo={veiculo}
      mode="interactive"
      volumeAlocar={Number(volume) || 0}
      compartimentosSel={compartimentosSel}
      itensOrdem={itensOrdem}
      onToggleCompartimento={toggleCompartimento}
      onIncompatibleClick={handleBocaIncompativel}
    />
  );

  const ResumoLateral = () => (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      bg="white"
      overflow="hidden"
    >
      <Box bg="gray.50" px={4} py={3} borderBottom="1px solid" borderColor="gray.200">
        <Text fontWeight="bold" color="#007abe" fontSize="sm">
          Resumo da ordem
        </Text>
      </Box>
      <VStack align="stretch" p={4} spacing={3} fontSize="sm">
        <Box>
          <Text {...fieldLabelProps}>Cliente</Text>
          <Text fontWeight="medium">
            {rowItem?.cliente || rowItem?.nome || rowItem?.contato || "—"}
          </Text>
        </Box>
        <Box>
          <Text {...fieldLabelProps}>Data</Text>
          <Text fontWeight="medium">
            {dataCarregamento
              ? new Date(dataCarregamento + "T12:00:00").toLocaleDateString("pt-BR")
              : "—"}
          </Text>
        </Box>
        <Box>
          <Text {...fieldLabelProps}>
            Placa{veiculosEscolhidos.length > 1 ? "s" : ""}
          </Text>
          <Text fontWeight="medium">
            {veiculosEscolhidos.map((v) => v.placa).join(", ") || "—"}
          </Text>
          {veiculosEscolhidos.length > 1 && (
            <Text fontSize="xs" color="gray.500">
              {veiculosEscolhidos.length} caminhões · cap.{" "}
              {capacidadeTotalFrota.toLocaleString("pt-BR")} L
            </Text>
          )}
        </Box>
        <Box>
          <Text {...fieldLabelProps}>Motorista(s)</Text>
          <Text fontWeight="medium">
            {veiculosIds
              .map((id) => motoristasMap[id]?.nome)
              .filter(Boolean)
              .join(", ") || "—"}
          </Text>
        </Box>
        <Divider />
        {volumeTotalPedido > 0 && (
          <Box>
            <Text {...fieldLabelProps}>Total do pedido</Text>
            <Text fontWeight="bold" color="#007abe" fontSize="lg">
              {volumeTotalPedido.toLocaleString("pt-BR")} L
            </Text>
            <Text fontSize="xs" color="gray.500">
              Litros a alocar neste carregamento
            </Text>
          </Box>
        )}
        <Box>
          <Text {...fieldLabelProps}>Volume alocado</Text>
          <Text fontWeight="bold" color="green.600" fontSize="lg">
            {volumeTotalOrdem.toLocaleString("pt-BR")} L
          </Text>
          {volumeTotalPedido > 0 && (
            <Text
              fontSize="xs"
              color={volumeRestantePedido > 0 ? "orange.600" : "green.600"}
              mt={1}
            >
              {volumeRestantePedido > 0
                ? `Faltam ${volumeRestantePedido.toLocaleString("pt-BR")} L`
                : "Pedido totalmente alocado"}
            </Text>
          )}
        </Box>
        <Box>
          <Text {...fieldLabelProps}>Itens</Text>
          <Text fontWeight="medium">{itensOrdem.length}</Text>
        </Box>
        {compartimentosSel.length > 0 && step === 3 && (
          <Badge colorScheme="orange">
            Selecionados: C{compartimentosSel.join(", C")}
          </Badge>
        )}
      </VStack>
    </Box>
  );

  const renderStepContent = () => {
    if (ordemConfirmada) {
      return (
        <Box>
          <Box
            bgGradient="linear(to-r, #007abe, #005a8a)"
            color="white"
            p={5}
            borderRadius="lg"
            mb={4}
          >
            <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={3}>
              <Box>
                <Text fontSize="xs" opacity={0.9}>
                  ORDEM DE CARREGAMENTO
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  #{ordemConfirmada.numero}
                </Text>
              </Box>
              <Box textAlign="right">
                <Text fontSize="xs" opacity={0.9}>
                  Previsão
                </Text>
                <Text fontWeight="bold">
                  {new Date(
                    ordemConfirmada.data + "T12:00:00",
                  ).toLocaleDateString("pt-BR")}
                </Text>
              </Box>
            </Flex>
          </Box>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} mb={4}>
            <Box p={4} bg="gray.50" borderRadius="lg">
              <Text {...fieldLabelProps}>Frota</Text>
              <Text fontWeight="bold">{ordemConfirmada.placa}</Text>
              <Text {...fieldLabelProps} mt={2}>
                Motorista(s)
              </Text>
              <Text fontWeight="medium">{ordemConfirmada.motorista}</Text>
            </Box>
            <Box p={4} bg="gray.50" borderRadius="lg">
              <Text {...fieldLabelProps}>Cliente</Text>
              <Text fontWeight="medium">{ordemConfirmada.cliente}</Text>
              <Text fontSize="xs" color="gray.500">
                Cód. {ordemConfirmada.clienteCodigo}
              </Text>
            </Box>
          </Grid>
          <Table size="sm" variant="simple">
            <Thead bg="#007abe">
              <Tr>
                <Th color="white">Produto</Th>
                <Th color="white">Fornecedor</Th>
                <Th color="white">Placa</Th>
                <Th color="white">Volume</Th>
                <Th color="white">Compartimentos</Th>
              </Tr>
            </Thead>
            <Tbody>
              {ordemConfirmada.itens.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.produtoDesc}</Td>
                  <Td>{item.fornecedor || "—"}</Td>
                  <Td>{item.placa || "—"}</Td>
                  <Td fontWeight="semibold">
                    {item.volume.toLocaleString("pt-BR")} L
                  </Td>
                  <Td>
                    <Badge colorScheme="blue">
                      C{item.compartimentos.join(", C")}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Button
            mt={4}
            variant="outline"
            colorScheme="blue"
            onClick={() => {
              setOrdemConfirmada(null);
              setItensOrdem([]);
              setStep(1);
            }}
          >
            Nova ordem de carregamento
          </Button>
        </Box>
      );
    }

    switch (step) {
      case 1:
        return (
          <Box>
            <Text fontWeight="bold" color="gray.800" mb={1}>
              Selecione a frota (um ou mais caminhões)
            </Text>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Busque pela placa e adicione quantos veículos forem necessários
              para a carga.
            </Text>
            {loadingFrota ? (
              <Center py={8}>
                <Spinner color="#007abe" />
              </Center>
            ) : frotaErro ? (
              <Text fontSize="sm" color="red.500">
                {frotaErro}
              </Text>
            ) : (
              <>
                <FormControl maxW="480px" mb={4}>
                  <FormLabel {...fieldLabelProps}>Adicionar placa</FormLabel>
                  <VeiculoAutocomplete
                    key={`add-veiculo-${veiculosIds.length}`}
                    veiculos={veiculos.filter(
                      (v) => !veiculosIds.includes(v.id),
                    )}
                    value=""
                    onChange={adicionarVeiculo}
                    placeholder="Digite a placa ou selecione"
                    inputProps={inputProps}
                  />
                </FormControl>

                {veiculosEscolhidos.length > 0 ? (
                  <Box mb={4}>
                    <Text {...fieldLabelProps} mb={2}>
                      Selecionados ({veiculosEscolhidos.length})
                    </Text>
                    <Wrap spacing={2}>
                      {veiculosEscolhidos.map((v) => (
                        <WrapItem key={v.id}>
                          <Tag
                            size="lg"
                            borderRadius="full"
                            variant={
                              veiculoAtivoId === v.id ? "solid" : "subtle"
                            }
                            colorScheme="blue"
                            cursor="pointer"
                            onClick={() => {
                              setVeiculoAtivoId(v.id);
                              setCompartimentosSel([]);
                            }}
                          >
                            <TagLabel>
                              {v.placa}
                              {v.bocas ? ` · ${v.bocas} bocas` : ""}
                            </TagLabel>
                            <TagCloseButton
                              onClick={(e) => {
                                e.stopPropagation();
                                removerVeiculo(v.id);
                              }}
                            />
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Capacidade total da frota:{" "}
                      {capacidadeTotalFrota.toLocaleString("pt-BR")} L
                    </Text>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="gray.500" mb={4}>
                    Nenhum caminhão adicionado ainda.
                  </Text>
                )}
              </>
            )}
            {veiculo && (
              <Box mt={2}>
                <Text fontSize="sm" fontWeight="semibold" color="#007abe" mb={2}>
                  Compartimentos — {veiculo.placa}
                </Text>
                <TruckCompartmentsVisual
                  veiculo={veiculo}
                  mode="capacity"
                  showLegend
                />
              </Box>
            )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Text fontWeight="bold" color="gray.800" mb={1}>
              Motorista e data do carregamento
            </Text>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Confirme o motorista de cada caminhão selecionado.
            </Text>
            <VStack spacing={4} align="stretch" mb={4}>
              {veiculosEscolhidos.map((v) => {
                const mot = motoristasMap[v.id] || {};
                return (
                  <Box
                    key={v.id}
                    p={4}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    bg="white"
                  >
                    <HStack mb={3} justify="space-between">
                      <Text fontWeight="bold" color="gray.800">
                        {v.placa}
                      </Text>
                      {mot.automatico && (
                        <Badge colorScheme="green">Vínculo automático</Badge>
                      )}
                    </HStack>
                    <FormControl isRequired>
                      <FormLabel {...fieldLabelProps}>
                        {mot.automatico
                          ? "Motorista (automático)"
                          : "Motorista"}
                      </FormLabel>
                      {mot.automatico ? (
                        <>
                          <Input
                            {...inputProps}
                            value={mot.nome || ""}
                            isReadOnly
                            bg="gray.100"
                          />
                          {mot.cpf && (
                            <Text fontSize="xs" color="gray.500" mt={2}>
                              CPF: {mot.cpf}
                            </Text>
                          )}
                        </>
                      ) : (
                        <>
                          <Select
                            {...inputProps}
                            placeholder="Selecione o motorista"
                            value={mot.id || ""}
                            onChange={(e) => {
                              const id = e.target.value;
                              const m = motoristas.find(
                                (x) => String(x.id) === String(id),
                              );
                              setMotoristaDoVeiculo(
                                v.id,
                                m
                                  ? {
                                      id: m.id,
                                      nome: m.nome || "",
                                      cpf: m.cpf || "",
                                    }
                                  : null,
                              );
                            }}
                          >
                            {motoristas.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.nome}
                                {m.cpf ? ` — ${m.cpf}` : ""}
                              </option>
                            ))}
                          </Select>
                          <Text fontSize="xs" color="gray.500" mt={2}>
                            Nenhum motorista vinculado a este veículo. Selecione
                            na lista (cadastro de frota).
                          </Text>
                        </>
                      )}
                    </FormControl>
                  </Box>
                );
              })}
            </VStack>
            <FormControl isRequired maxW="320px">
              <FormLabel {...fieldLabelProps}>
                <HStack spacing={2}>
                  <Icon as={FaCalendarAlt} />
                  <Text>Data do carregamento</Text>
                </HStack>
              </FormLabel>
              <Input
                type="date"
                {...inputProps}
                value={dataCarregamento}
                onChange={(e) => setDataCarregamento(e.target.value)}
              />
            </FormControl>
          </Box>
        );
      case 3:
        return (
          <Box>
            <Text fontWeight="bold" color="gray.800" mb={1}>
              Produto, volume e compartimentos
            </Text>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Produtos do histórico de preços de hoje ({todayBR()})
            </Text>

            {veiculosEscolhidos.length > 1 && (
              <FormControl mb={4} maxW="420px">
                <FormLabel {...fieldLabelProps}>
                  Caminhão para esta alocação
                </FormLabel>
                <Select
                  {...inputProps}
                  value={veiculoAtivoId}
                  onChange={(e) => {
                    setVeiculoAtivoId(e.target.value);
                    setCompartimentosSel([]);
                  }}
                >
                  {veiculosEscolhidos.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.placa}
                      {v.bocas ? ` (${v.bocas} bocas)` : ""}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            <VStack spacing={3} align="stretch" mb={6}>
              <FormControl isRequired>
                <FormLabel {...fieldLabelProps}>
                  Produto (preços de {todayBR()})
                </FormLabel>
                {loadingPrecos ? (
                  <HStack py={2}>
                    <Spinner size="sm" color="#007abe" />
                    <Text fontSize="sm" color="gray.500">
                      Carregando combustíveis do dia...
                    </Text>
                  </HStack>
                ) : produtosDisponiveis.length === 0 ? (
                  <Text fontSize="sm" color="orange.600">
                    Nenhum preço cadastrado para hoje ({todayBR()}). Cadastre em
                    Histórico de Preços ou informe a descrição manualmente
                    abaixo.
                  </Text>
                ) : (
                  <Select
                    {...inputProps}
                    placeholder="Selecione o combustível / produto"
                    value={produtoCodigo}
                    onChange={(e) => {
                      const keyOrCod = e.target.value;
                      setProdutoCodigo(keyOrCod);
                      const p = produtosDisponiveis.find(
                        (x) =>
                          String(x.key) === String(keyOrCod) ||
                          String(x.codigo) === String(keyOrCod),
                      );
                      setProdutoDesc(p?.descricao || "");
                      setProdutoPrecoSel(p || null);
                    }}
                  >
                    {produtosDisponiveis.map((p) => (
                      <option key={p.key || p.codigo} value={p.key || p.codigo}>
                        {p.descricao}
                        {p.fornecedor ? ` — ${p.fornecedor}` : ""}
                        {p.precoUnitario != null
                          ? ` · R$ ${Number(p.precoUnitario).toLocaleString(
                              "pt-BR",
                              {
                                minimumFractionDigits: 4,
                                maximumFractionDigits: 4,
                              },
                            )}`
                          : ""}
                      </option>
                    ))}
                  </Select>
                )}
              </FormControl>

              <Grid
                templateColumns={{ base: "1fr", md: "1fr minmax(240px, 300px)" }}
                gap={4}
                alignItems="start"
              >
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel {...fieldLabelProps}>Descrição</FormLabel>
                    <Input
                      {...inputProps}
                      placeholder="Descrição do produto"
                      value={produtoDesc}
                      onChange={(e) => {
                        setProdutoDesc(e.target.value);
                        if (!e.target.value) setProdutoPrecoSel(null);
                      }}
                    />
                  </FormControl>
                  {produtoPrecoSel?.precoUnitario != null && (
                    <Text
                      fontSize="sm"
                      color="#007abe"
                      fontWeight="medium"
                      mt={2}
                    >
                      Preço do dia: R${" "}
                      {Number(produtoPrecoSel.precoUnitario).toLocaleString(
                        "pt-BR",
                        {
                          minimumFractionDigits: 4,
                          maximumFractionDigits: 4,
                        },
                      )}
                      {produtoPrecoSel.fornecedor
                        ? ` · ${produtoPrecoSel.fornecedor}`
                        : ""}
                    </Text>
                  )}
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel {...fieldLabelProps}>Volume (L)</FormLabel>
                    <HStack spacing={2} align="center">
                      <IconButton
                        aria-label="Remover 1.000 litros"
                        icon={<FaMinus />}
                        size="sm"
                        h={inputProps.h || "40px"}
                        variant="outline"
                        borderColor="#007abe"
                        color="#007abe"
                        _hover={{ bg: "#e6f4fb" }}
                        onClick={() => adjustVolume(-1000)}
                        isDisabled={!(Number(volume) > 0)}
                      />
                      <Input
                        type="number"
                        min={0}
                        step={1000}
                        {...inputProps}
                        placeholder="Ex: 10000"
                        value={volume}
                        onChange={(e) => setVolume(e.target.value)}
                        textAlign="center"
                      />
                      <IconButton
                        aria-label="Adicionar 1.000 litros"
                        icon={<FaPlus />}
                        size="sm"
                        h={inputProps.h || "40px"}
                        bg="#007abe"
                        color="white"
                        _hover={{ bg: "#005a8a" }}
                        onClick={() => adjustVolume(1000)}
                      />
                    </HStack>
                  </FormControl>
                </GridItem>
              </Grid>
            </VStack>

            <Text fontSize="sm" fontWeight="semibold" color="#007abe" mb={2}>
              Alocar nos compartimentos
              {veiculo?.placa ? ` — ${veiculo.placa}` : ""}
            </Text>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Selecione as bocas para esta carga
              {produtoDesc || volume ? (
                <>
                  {" "}
                  — <strong>{produtoDesc || "—"}</strong>
                  {Number(volume) > 0
                    ? ` · ${Number(volume).toLocaleString("pt-BR")} L`
                    : ""}
                </>
              ) : null}
            </Text>
            <TruckVisual />
            {Number(volume) > 0 &&
              veiculo?.litros_por_boca?.some(
                (cap) => Number(volume) > Number(cap),
              ) && (
                <Alert status="warning" mt={3} borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  <AlertDescription>
                    Volume de{" "}
                    <strong>
                      {Number(volume).toLocaleString("pt-BR")} L
                    </strong>{" "}
                    excede a capacidade de uma ou mais bocas (destacadas em
                    vermelho). Selecione apenas compartimentos compatíveis.
                  </AlertDescription>
                </Alert>
              )}
            <Button
              mt={4}
              leftIcon={<FaPlus />}
              bg="#007abe"
              color="white"
              _hover={{ bg: "#005a8a" }}
              onClick={adicionarItem}
              isDisabled={
                !produtoDesc ||
                Number(volume) <= 0 ||
                compartimentosSel.length === 0 ||
                compartimentosSel.some((n) =>
                  isBocaIncompativel(n, Number(volume)),
                )
              }
            >
              Adicionar à ordem
            </Button>

            {itensOrdem.length > 0 && (
              <Box mt={6}>
                <Text fontSize="sm" fontWeight="bold" color="#007abe" mb={2}>
                  Itens incluídos ({itensOrdem.length})
                </Text>
                <Table size="sm">
                  <Thead bg="gray.100">
                    <Tr>
                      <Th>Produto</Th>
                      <Th>Fornecedor</Th>
                      <Th>Placa</Th>
                      <Th>Volume</Th>
                      <Th>Comp.</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {itensOrdem.map((item) => (
                      <Tr key={item.id}>
                        <Td>{item.produtoDesc}</Td>
                        <Td>{item.fornecedor || "—"}</Td>
                        <Td>{item.placa || "—"}</Td>
                        <Td>{item.volume.toLocaleString("pt-BR")} L</Td>
                        <Td>C{item.compartimentos.join(", C")}</Td>
                        <Td>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() =>
                              setItensOrdem((p) =>
                                p.filter((x) => x.id !== item.id),
                              )
                            }
                          >
                            <FaTrash />
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        );
      case 4:
        return (
          <Box>
            <Text fontWeight="bold" color="gray.800" mb={4}>
              Revisão final
            </Text>
            <Table size="sm" mb={4}>
              <Thead bg="#007abe">
                <Tr>
                  <Th color="white">Produto</Th>
                  <Th color="white">Fornecedor</Th>
                  <Th color="white">Placa</Th>
                  <Th color="white">Volume</Th>
                  <Th color="white">Compartimentos</Th>
                  <Th color="white">Motorista</Th>
                  <Th color="white">Data</Th>
                </Tr>
              </Thead>
              <Tbody>
                {itensOrdem.length === 0 ? (
                  <Tr>
                    <Td colSpan={7} textAlign="center" color="gray.500" py={6}>
                      Nenhum item — volte e adicione produtos
                    </Td>
                  </Tr>
                ) : (
                  itensOrdem.map((item) => (
                    <Tr key={item.id}>
                      <Td>{item.produtoDesc}</Td>
                      <Td>{item.fornecedor || "—"}</Td>
                      <Td>{item.placa || "—"}</Td>
                      <Td>{item.volume.toLocaleString("pt-BR")} L</Td>
                      <Td>C{item.compartimentos.join(", C")}</Td>
                      <Td>
                        {item.motoristaNome ||
                          motoristasMap[item.veiculoId]?.nome ||
                          "—"}
                      </Td>
                      <Td>
                        {new Date(
                          dataCarregamento + "T12:00:00",
                        ).toLocaleDateString("pt-BR")}
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
            <Button
              size="lg"
              w="full"
              bg="#c0a316"
              color="white"
              _hover={{ bg: "#9d8011" }}
              leftIcon={<FaGasPump />}
              onClick={confirmarOrdem}
              isDisabled={itensOrdem.length === 0}
            >
              Confirmar ordem de carregamento
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      bg="white"
      borderRadius="10px"
      shadow="lg"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
    >
      <Box
        bgGradient="linear(to-r, #007abe, #005a8a)"
        color="white"
        px={5}
        py={3}
      >
        <Flex align="center" gap={3}>
          <Icon as={MdLocalShipping} boxSize={6} />
          <Box>
            <Text fontSize="lg" fontWeight="bold">
              Ordem de Carregamento
            </Text>
            <Text fontSize="xs" opacity={0.9}>
              Volume · Produto · Compartimento · Motorista · Placa · Data
            </Text>
          </Box>
        </Flex>
      </Box>

      <Grid
        templateColumns={{ base: "1fr", lg: "220px 1fr 260px" }}
        gap={0}
        minH="520px"
      >
        <Box p={4} borderRight="1px solid" borderColor="gray.200" bg="gray.50">
          <StepperLateral />
        </Box>
        <Box p={5}>{renderStepContent()}</Box>
        <Box
          p={4}
          borderLeft="1px solid"
          borderColor="gray.200"
          bg="gray.50"
          display={{ base: "none", lg: "block" }}
        >
          <ResumoLateral />
        </Box>
      </Grid>

      {!ordemConfirmada && (
        <Flex
          px={5}
          py={4}
          borderTop="1px solid"
          borderColor="gray.200"
          justify="space-between"
          bg="gray.50"
        >
          <HStack spacing={3}>
            <Button
              variant="outline"
              isDisabled={step <= 1}
              onClick={() => setStep((s) => Math.max(1, s - 1))}
            >
              Voltar
            </Button>
            {isLocalhostDev() && (
              <Badge colorScheme="orange" fontSize="xs" variant="subtle">
                Teste local: etapas sem validação
              </Badge>
            )}
          </HStack>
          {step < TOTAL_STEPS ? (
            <Button
              bg="#007abe"
              color="white"
              _hover={{ bg: "#005a8a" }}
              isDisabled={!canNext()}
              onClick={() => setStep((s) => Math.min(TOTAL_STEPS, s + 1))}
            >
              Próximo
            </Button>
          ) : null}
        </Flex>
      )}
    </Box>
  );
};

export default OrdemCarregamentoPanel;
